import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import Divider from '@mui/material/Divider';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import ExpandableTextArea from 'components/ExpandableTextArea';
import CodeInput from 'components/inputs/CodeInput';

/**
 * Component for displaying and managing the details of an prompt. Allows viewing,
 * creating, and updating prompts.
 *
 * @component
 *
 * @example
 * <PromptDetails prompt={prompt} onSubmit={handleSubmit} />
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.prompt - The API key object to be displayed and edited.
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * @param {Function} props.onFieldChange - The function to call when the fields value change.
 *
 * @returns {React.Component} The API Key Details component.
 */
function PromptDetails({ prompt, onSubmit, onFieldChange }) {
  const { formatMessage: msg } = useIntl();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      id: '',
      code: '',
      type: '',
      content: '',
      description: ''
    }
  });

  watch();

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(isDirty);
    }
  }, [isDirty, onFieldChange]);

  const resetPrompt = useCallback(() => {
    if (prompt?.code) {
      reset(prompt);
    } else {
      reset({
        id: '',
        code: '',
        type: '',
        content: '',
        description: ''
      });
    }
  }, [prompt, reset]);

  useEffect(() => {
    resetPrompt();
  }, [prompt, resetPrompt]);

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  const handleReset = () => {
    resetPrompt();
  };

  const isCreating = !prompt?.id;

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      display="flex"
      direction="column"
      sx={{ height: '100%' }}
    >
      <Stack display="flex" direction="row">
        <SpeakerNotesIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
        <MDTypography variant="h5">
          {msg({
            id: isCreating
              ? 'page.prompts.details.creation.title'
              : 'page.prompts.details.modification.title'
          })}
        </MDTypography>
      </Stack>
      <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />
      <Stack
        spacing={1}
        display="flex"
        direction="column"
        flex={1}
        overflow="auto"
        mb={2}
        sx={{ scrollbarWidth: 'thin' }}
      >
        {!isCreating && (
          <Controller
            name="id"
            control={control}
            render={({ field }) => (
              <MDInput
                {...field}
                label={msg({ id: 'field.id.label' })}
                variant="filled"
                fullWidth
                required
                size="small"
                InputProps={{
                  readOnly: true
                }}
              />
            )}
          />
        )}
        <Controller
          name="code"
          control={control}
          rules={{ required: msg({ id: 'field.code.required' }) }}
          render={({ field }) => (
            <CodeInput field={field} errors={errors} clearField={() => clearField('code')} />
          )}
        />
        <Controller
          name="type"
          control={control}
          rules={{ required: msg({ id: 'field.prompt.type.required' }) }}
          render={({ field }) => (
            <FormControl variant="filled" fullWidth error={!!errors.systemPrompt}>
              <InputLabel id="system-prompt-label">
                {msg({ id: 'field.prompt.type.label' })}
              </InputLabel>
              <Select
                {...field}
                label={msg({ id: 'field.prompt.type.label' })}
                size="small"
                required
              >
                {Object.values(PromptType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {msg({ id: `field.prompt.type.${type}.label` })}
                  </MenuItem>
                ))}
              </Select>
              {errors.systemPrompt && (
                <FormHelperText>{errors.systemPrompt.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.description.label' })}
              variant="filled"
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('description')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="content"
          control={control}
          rules={{ required: msg({ id: 'field.content.required' }) }}
          render={({ field }) => (
            <ExpandableTextArea
              field={field}
              error={!!errors.content}
              helperText={errors.content ? errors.content.message : ''}
              clearField={() => clearField('content')}
              label={msg({ id: 'field.content.label' })}
              control={control}
            />
          )}
        />
      </Stack>
      <Divider sx={{ mt: 0 }} />
      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton type="submit" fullWidth color="primary">
          {isCreating ? msg({ id: 'button.create' }) : msg({ id: 'button.update' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

export const PromptType = {
  SYSTEM: 'SYSTEM',
  ADDITIONAL_SYSTEM: 'ADDITIONAL_SYSTEM',
  SYSTEM_MEMORY: 'SYSTEM_MEMORY'
};

PromptDetails.propTypes = {
  prompt: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    type: PropTypes.string,
    content: PropTypes.string,
    description: PropTypes.string
  }),
  onSubmit: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired
};

export default PromptDetails;
